/**
 * THE REPORT FRAME — the one scaffold the four Figma 06 reports share (v19 D4).
 *
 * Header with the SERVER's total · the period filter (chips + custom dates + the
 * `resolved-range` sentence saying what was USED) · the work-type narrowing · an optional
 * extra control (the Parties sort) · the EXPORT button · a summary strip · the table ·
 * the served disclosures · the honest-count pager. Every state a register carries:
 * skeleton, error with "Try again.", an empty page of a non-empty report ("page 4 is past
 * the end"), an empty window, and the D5 refusal (a designed Alert naming who to ask,
 * never the key).
 *
 * ===========================================================================
 * THE EXPORT IS A CHANNEL — rule 14, in one place for all four
 * ===========================================================================
 * "Export CSV" is rendered ONLY for a seat holding `reports.export` (Country Manager,
 * Accounts — not Dev Head, who reads the reports and has no Export button; proposal §6
 * flags it). It serialises THE ROWS ON SCREEN through each column's `csv(row)` — the same
 * string the cell renders, which for money is `formatMoney(...)` = `'•••'` for a masked
 * seat — via `lib/csv.ts`. A Developer's CSV cannot contain a digit the seat was refused
 * because no such digit exists on this side of the wire.
 *
 * Each page stays `crf-register-row` on its data rows so the density ruling covers it.
 */

import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { downloadCsv, rowsToCsv } from '../../lib/csv';
import { PeriodFilter } from '../dashboards/PeriodFilter';
import { REPORT_WORK_TYPES, reportsErrorText } from './api';
import type { Paged, ReportWindowDto } from './api';
import { useReportUrl } from './report-filters';

export interface ReportColumn<Row> {
  readonly key: string;
  readonly header: string;
  readonly cell: (row: Row) => ReactNode;
  /** The VISIBLE string for the export — never a raw field. */
  readonly csv: (row: Row) => string;
  readonly width?: string;
  readonly numeric?: boolean;
}

export type ReportData<Row> = Paged<Row> & {
  readonly window: ReportWindowDto | null;
  readonly disclosures: readonly string[];
};

export function ReportFrame<Row>({
  title,
  testId,
  refusal,
  query,
  columns,
  rowKey,
  noun,
  summary,
  extraControls,
  periodAllowAll = false,
  canExport,
  filename,
  emptyTitle,
}: {
  title: string;
  testId: string;
  /** When set, the designed refusal renders and nothing else does. */
  refusal: { title: string; body: string } | null;
  query: UseQueryResult<ReportData<Row>>;
  columns: readonly ReportColumn<Row>[];
  rowKey: (row: Row) => string;
  /** ['document', 'documents'] — the subtitle's unit. */
  noun: readonly [string, string];
  summary?: (data: ReportData<Row>) => ReactNode;
  extraControls?: ReactNode;
  periodAllowAll?: boolean;
  canExport: boolean;
  filename: string;
  emptyTitle: string;
}): React.JSX.Element {
  const { workType, setWorkType, goToPage } = useReportUrl();

  if (refusal !== null) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title={refusal.title}>
          {refusal.body}
        </Alert>
      </div>
    );
  }

  const rows = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  const exportCsv = (): void => {
    if (rows.length === 0) return;
    downloadCsv(
      filename,
      rowsToCsv(
        columns.map((column) => column.header),
        rows.map((row) => columns.map((column) => column.csv(row))),
      ),
    );
  };

  return (
    <div className="flex flex-col gap-4 p-6" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-h1 text-fg">{title}</h1>
          {query.data === undefined ? null : (
            <p className="text-label text-fg">
              {/* The SERVER'S total, never the length of this page. */}
              {String(total)} {total === 1 ? noun[0] : noun[1]}
            </p>
          )}
        </div>
        {canExport ? (
          <button
            type="button"
            onClick={exportCsv}
            disabled={rows.length === 0}
            className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-sm border border-line bg-surface px-2.5 text-body13 font-medium text-fg hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-40"
          >
            Export CSV
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <PeriodFilter
          id={testId}
          allowAll={periodAllowAll}
          resolved={query.data === undefined ? undefined : query.data.window}
        />
        <div className="flex items-center gap-2">
          <label className="text-body13 text-fg-secondary" htmlFor={`${testId}-work-type`}>
            Work
          </label>
          <select
            id={`${testId}-work-type`}
            value={workType}
            onChange={(event) => setWorkType(event.target.value === '' ? null : event.target.value)}
            className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
          >
            {REPORT_WORK_TYPES.map((entry) => (
              <option key={entry.value} value={entry.value === 'all' ? '' : entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>
        {extraControls}
      </div>

      {query.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label={`Loading ${title}`} />
        </div>
      ) : query.isError ? (
        <Alert tone="danger" title={`${title} could not be loaded`}>
          {/* The server's own sentence — on a 422 it names the vocabulary. */}
          <p>{reportsErrorText(query.error)}</p>
          <button
            type="button"
            onClick={() => {
              void query.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : (
        <>
          {summary === undefined ? null : summary(query.data)}

          {rows.length === 0 && total > 0 ? (
            <Alert tone="info" title={`Page ${String(query.data.page)} is past the end of this report`}>
              <p>
                The report holds {String(total)} {total === 1 ? noun[0] : noun[1]} in this window.
              </p>
              <button
                type="button"
                onClick={() => goToPage(1)}
                className="mt-1 font-medium underline underline-offset-2"
              >
                Show the first page.
              </button>
            </Alert>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-line bg-surface">
              <EmptyState title={emptyTitle} />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-line bg-surface">
              <table className="w-full min-w-[1100px] border-collapse text-left">
                <caption className="sr-only">{title}</caption>
                <colgroup>
                  {columns.map((column) => (
                    <col key={column.key} className={column.width} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="h-10 border-b border-line bg-subtle">
                    {columns.map((column) => (
                      <th key={column.key} scope="col" className="px-3 text-th uppercase text-fg">
                        {column.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={rowKey(row)} className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle">
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={column.numeric === true ? 'tnum px-3 align-middle text-body13 text-fg' : 'px-3 align-middle text-body13 text-fg'}
                        >
                          {column.cell(row)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* SERVED disclosures — what this report is and is not, in the server's words. */}
          {query.data.disclosures.length === 0 ? null : (
            <ul className="flex flex-col gap-1" data-testid="disclosures">
              {query.data.disclosures.map((line) => (
                <li key={line} className="text-label text-fg-secondary">
                  {line}
                </li>
              ))}
            </ul>
          )}

          {rows.length > 0 ? (
            <PagerFooter
              page={query.data.page}
              perPage={query.data.perPage}
              shown={rows.length}
              total={query.data.total}
              onPage={goToPage}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── pager ── */

/** `Table/Pagination` — re-authored per screen family, the house pattern. */
function PagerFooter({
  page,
  perPage,
  shown,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  shown: number;
  total: number;
  onPage: (page: number) => void;
}): React.JSX.Element {
  const firstOnPage = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastOnPage = (page - 1) * perPage + shown;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
      <p className="text-body13 text-fg-secondary">{perPage} per page</p>
      <div className="flex items-center gap-2">
        <p className="tnum text-body13 text-fg-secondary">
          {firstOnPage}–{lastOnPage} of {total}
        </p>
        <button
          type="button"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
        </button>
        <button
          type="button"
          aria-label="Next page"
          disabled={lastOnPage >= total}
          onClick={() => onPage(page + 1)}
          className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </button>
      </div>
    </div>
  );
}
